import { useState } from "react";
import { toast } from "sonner";
import { Plus, X, Sparkle } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

const inputCls = "w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink";
const lbl = "text-[11px] font-bold uppercase tracking-widest text-stone2";

/**
 * Admin editor for a campaign's Talking Points + Objection Handling.
 * Leaving both empty makes the app fall back to smart defaults (by purpose).
 */
export const ScriptEditor = ({ talkingPoints = [], objections = [], onChange, purpose, name, description }) => {
    const [generating, setGenerating] = useState(false);

    const setPoints = (pts) => onChange({ talking_points: pts, objections });
    const setObjections = (objs) => onChange({ talking_points: talkingPoints, objections: objs });

    const addPoint = () => setPoints([...talkingPoints, ""]);
    const editPoint = (i, v) => setPoints(talkingPoints.map((p, idx) => (idx === i ? v : p)));
    const removePoint = (i) => setPoints(talkingPoints.filter((_, idx) => idx !== i));

    const addObjection = () => setObjections([...objections, { objection: "", response: "" }]);
    const editObjection = (i, key, v) => setObjections(objections.map((o, idx) => (idx === i ? { ...o, [key]: v } : o)));
    const removeObjection = (i) => setObjections(objections.filter((_, idx) => idx !== i));

    const generate = async () => {
        setGenerating(true);
        try {
            const { data } = await api.post("/campaigns/suggest-script", {
                purpose: purpose || "sales", name: name || "", description: description || "",
            });
            onChange({
                talking_points: data.talking_points || [],
                objections: data.objections || [],
            });
            toast.success("AI drafted a script — edit it as you like before saving.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "AI generation failed");
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div className="space-y-5" data-testid="script-editor">
            <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-stone2">Shown to agents during the call. Leave blank to use smart defaults based on the campaign purpose.</p>
                <button
                    type="button"
                    onClick={generate}
                    disabled={generating}
                    data-testid="script-generate-ai"
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-indigo-300 bg-indigo-50 px-3.5 py-1.5 text-xs font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 disabled:opacity-60"
                >
                    <Sparkle size={14} weight="fill" /> {generating ? "Generating…" : "Generate with AI"}
                </button>
            </div>

            <div>
                <label className={lbl}>Talking Points</label>
                <div className="mt-2 space-y-2" data-testid="talking-points-list">
                    {talkingPoints.length === 0 && (
                        <p className="text-xs italic text-stone2">No custom points — smart defaults will be used.</p>
                    )}
                    {talkingPoints.map((p, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <input
                                value={p}
                                onChange={(e) => editPoint(i, e.target.value)}
                                data-testid={`talking-point-${i}`}
                                placeholder="e.g. Lead with the key benefit relevant to them."
                                className={inputCls}
                            />
                            <button type="button" onClick={() => removePoint(i)} data-testid={`talking-point-remove-${i}`} className="shrink-0 rounded-lg p-1.5 text-stone2 hover:bg-stone-100 hover:text-red-500">
                                <X size={16} />
                            </button>
                        </div>
                    ))}
                </div>
                <button type="button" onClick={addPoint} data-testid="talking-point-add" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-ink hover:underline">
                    <Plus size={14} /> Add talking point
                </button>
            </div>

            <div>
                <label className={lbl}>Objection Handling</label>
                <div className="mt-2 space-y-3" data-testid="objections-list">
                    {objections.length === 0 && (
                        <p className="text-xs italic text-stone2">No custom objections — smart defaults will be used.</p>
                    )}
                    {objections.map((o, i) => (
                        <div key={i} className="rounded-xl border border-stone-200 p-3">
                            <div className="flex items-center gap-2">
                                <input
                                    value={o.objection}
                                    onChange={(e) => editObjection(i, "objection", e.target.value)}
                                    data-testid={`objection-${i}`}
                                    placeholder={`Customer objection, e.g. "It's too expensive"`}
                                    className={`${inputCls} font-semibold`}
                                />
                                <button type="button" onClick={() => removeObjection(i)} data-testid={`objection-remove-${i}`} className="shrink-0 rounded-lg p-1.5 text-stone2 hover:bg-stone-100 hover:text-red-500">
                                    <X size={16} />
                                </button>
                            </div>
                            <textarea
                                value={o.response}
                                onChange={(e) => editObjection(i, "response", e.target.value)}
                                data-testid={`objection-response-${i}`}
                                rows={2}
                                placeholder="Suggested response the agent can say…"
                                className={`${inputCls} mt-2 resize-none`}
                            />
                        </div>
                    ))}
                </div>
                <button type="button" onClick={addObjection} data-testid="objection-add" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-ink hover:underline">
                    <Plus size={14} /> Add objection
                </button>
            </div>
        </div>
    );
};
